  import { getAuthToken } from "./auth"; // adjust path as needed

  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://stage.getrevibe.app/api";

  /**
   * Fetches the current user's active subscription details.
   * GET /me/subscription
   * Requires Bearer token authorization.

  */
  export const fetchUserSubscription = async () => {
    const token = getAuthToken();
    if (!token) throw new Error("No auth token found");

    const response = await fetch(`${API_BASE_URL}/me/subscription`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 404) return null; // user has no subscription
    if (!response.ok) throw new Error("Failed to fetch user subscription");

    const data = await response.json();

    return normalizeSubscription(data);
  };

  /**
   * Normalizes the raw API response into a clean, UI-friendly object.

  */
  const normalizeSubscription = (raw) => {
    const sub = raw.subscription || {};
    const admin = raw.appliedByAdmin || null;
    const payment = admin?.paymentDetails || null;

    return {
      // ── Record identifiers ──────────────────────────────────────────
      id: raw._id,
      userId: raw.user,

      // ── Plan details ────────────────────────────────────────────────
      plan: {
        id: sub._id,
        title: sub.title,
        duration: `${sub.duration_value} ${sub.duration_unit}${
          sub.duration_value > 1 ? "s" : ""
        }`,
        durationValue: sub.duration_value,
        durationUnit: sub.duration_unit,
        price: sub.price,
        currency: sub.currency || "INR",
        features:
          sub.features && sub.features.length > 0
            ? sub.features
            : ["No features listed"],
      },

      // ── Status & dates ──────────────────────────────────────────────
      status: raw.status, // e.g. "Active"
      activationDate: raw.activationDate ? new Date(raw.activationDate) : null,
      expiryDate: raw.expiryDate ? new Date(raw.expiryDate) : null,
      createdAt: raw.createdAt ? new Date(raw.createdAt) : null,
      updatedAt: raw.updatedAt ? new Date(raw.updatedAt) : null,

      // ── Razorpay ────────────────────────────────────────────────────
      razorpayOrderId: raw.razorpay_order_id || null,

      // ── Admin-applied metadata ──────────────────────────────────────
      appliedByAdmin: admin
        ? {
            name: admin.name,
            email: admin.email,
            reason: admin.reason,
            appliedAt: admin.appliedAt ? new Date(admin.appliedAt) : null,
            payment: payment
              ? {
                  paymentType: payment.payment_Type,
                  accountNumber: payment.account_number,
                  accountName: payment.account_name,
                  source: payment.source,
                }
              : null,
          }
        : null,

      // ── Add-ons ─────────────────────────────────────────────────────
      addons: Array.isArray(raw.addons)
        ? raw.addons.map((addon) => {
            let features = [];
            if (addon.description && addon.description.trim()) {
              features = addon.description
                .split(/[.\n]+/)
                .map((f) => f.trim())
                .filter((f) => f.length > 0 && !f.includes("http"));
            }
            if (features.length === 0) features = ["No features listed"];

            return {
              id: addon._id,
              title: addon.name,
              description: addon.description,
              price: addon.price,
              mrp: addon.mrp,
              originalPrice: addon.mrp || addon.price,
              discountedPrice: addon.price,
              durationInDays: addon.durationInDays,
              status: addon.status,
              features,
              createdAt: addon.createdAt ? new Date(addon.createdAt) : null,
              updatedAt: addon.updatedAt ? new Date(addon.updatedAt) : null,
            };
          })
        : [],
    };
  };